import { chain } from '@leprecoins/effector';
import { querySync } from 'atomic-router';
import { combine, createEvent, createStore, sample } from 'effector';
import { empty } from 'patronum';
import { accountTreeModel } from 'entities/account';
import { viewerModel } from 'entities/viewer';
import { AccountWithWalletsAndPermissions, createGetAccountQuery } from 'shared/api';
import { authorizedAccountRoute, controls } from 'shared/routing';

export type AccountPageTabValues =
	| 'wallets'
	| 'limits'
	| 'settings'
	| 'transactions'
	| 'accounts'
	| 'cashdesks'
	| 'users'
	| 'games'
	| 'game-settings';

const getAccountQuery = createGetAccountQuery();

const $accountId = accountTreeModel.$singleSelectedNode;

const $account = createStore<AccountWithWalletsAndPermissions | null>(null);

const $isCurrentAccount = combine(
	[$accountId, viewerModel.$accountId],
	([accountId, viewerAccountId]) => accountId !== null && accountId === viewerAccountId,
);

const $accountName = $account.map((account) => account?.name ?? null);

const $empty = empty($accountId);

const $currentTab = createStore<AccountPageTabValues>('wallets');

const $pending = combine(
	[$account, getAccountQuery.$pending, viewerModel.getAccountQuery.$pending],
	([account, selectedAccountPending, viewerAccountPending]) =>
		(!account && selectedAccountPending) || viewerAccountPending,
);

const changeTab = createEvent<AccountPageTabValues | null>();

const accountIdSet = $accountId.updates.filter({ fn: (id) => id !== null });

const accountIdCleared = $accountId.updates.filter({ fn: (id) => id === null });

querySync({
	source: { id: accountTreeModel.$singleSelectedNode },
	route: authorizedAccountRoute,
	controls,
});

sample({
	clock: authorizedAccountRoute.$query,
	fn: ({ id }) => Number(id) || null,
	target: $accountId,
});

chain(changeTab, $currentTab);

chain(accountIdSet, accountTreeModel.expandToNode);

sample({
	clock: accountIdSet,
	filter: authorizedAccountRoute.$isOpened,
	target: getAccountQuery.start,
});

sample({
	clock: accountIdSet,
	fn: (node) => [node],
	target: accountTreeModel.setSelectedNodes,
});

sample({
	clock: getAccountQuery.finished.success,
	fn: ({ result }) => result,
	target: $account,
});

sample({
	clock: [accountIdCleared, authorizedAccountRoute.closed],
	target: [$account.reinit, getAccountQuery.reset],
});

export const accountPageModel = {
	$currentTab,
	changeTab,
	$account,
	$accountName,
	$empty,
	$isCurrentAccount,
	$pending,
	accountIdSet,
	accountIdCleared,
	$accountId,
	getAccountQuery,
};
